import { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface NavItem {
    path?: string;
    label: string;
    icon: React.ReactNode;
    children?: { path: string; label: string; icon: React.ReactNode }[];
}

const Icon = ({ d, size = 18 }: { d: string; size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={d}/>
    </svg>
);

const NAV_ITEMS: NavItem[] = [
    {
        path: '/',
        label: 'Dashboard',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>,
    },
    {
        path: '/history',
        label: 'History',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    },
    {
        label: 'Settings',
        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>,
        children: [
            { path: '/settings/profile', label: 'Profile', icon: <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/> },
            { path: '/settings/security', label: 'Security', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg> },
            { path: '/settings/subscription', label: 'Subscription', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg> },
            { path: '/settings/billing', label: 'Billing', icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
            { path: '/settings/account', label: 'Account', icon: <Icon d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z"/> },
        ],
    },
];

interface SidebarProps {
    collapsed: boolean;
    setCollapsed: (v: boolean) => void;
    mobileOpen: boolean;
    setMobileOpen: (v: boolean) => void;
}

const Sidebar = ({ collapsed, setCollapsed, mobileOpen, setMobileOpen }: SidebarProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [settingsOpen, setSettingsOpen] = useState(location.pathname.startsWith('/settings'));

    useEffect(() => {
        if (location.pathname.startsWith('/settings')) setSettingsOpen(true);
    }, [location.pathname]);

    const isSettingsActive = location.pathname.startsWith('/settings');

    const handleLogout = () => { logout(); navigate('/login'); };

    const NavItemLink = ({ item }: { item: NavItem }) => {
        if (item.children) {
            return (
                <div>
                    <button
                        onClick={() => { setSettingsOpen(o => !o); if (!settingsOpen && !location.pathname.startsWith('/settings')) navigate('/settings/profile'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isSettingsActive ? 'bg-teal-500/20 text-teal-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
                        title={collapsed ? item.label : undefined}
                    >
                        <span className="flex-shrink-0">{item.icon}</span>
                        {!collapsed && (
                            <>
                                <span className="flex-1 text-[13px] font-semibold text-left">{item.label}</span>
                                <svg className={`w-3.5 h-3.5 transition-transform ${settingsOpen ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="m6 9 6 6 6-6"/></svg>
                            </>
                        )}
                    </button>
                    {settingsOpen && !collapsed && (
                        <div className="mt-0.5 space-y-0.5 ml-2">
                            {item.children.map(child => (
                                <NavLink key={child.path} to={child.path} onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all ${isActive ? 'bg-teal-500/20 text-teal-300' : 'text-gray-500 hover:bg-white/5 hover:text-gray-300'}`}>
                                    <span className="flex-shrink-0">{child.icon}</span>
                                    <span>{child.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            );
        }
        return (
            <NavLink to={item.path!} end={item.path === '/'} onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${isActive ? 'bg-teal-500/20 text-teal-300' : 'text-gray-400 hover:bg-white/5 hover:text-gray-200'}`}
                title={collapsed ? item.label : undefined}
            >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && <span className="text-[13px] font-semibold">{item.label}</span>}
            </NavLink>
        );
    };

    return (
        <>
            {/* Mobile overlay */}
            {mobileOpen && (
                <div className="fixed inset-0 bg-black/50 z-20 md:hidden" onClick={() => setMobileOpen(false)}/>
            )}

            <aside className={`
                fixed md:relative inset-y-0 left-0 z-30 flex flex-col
                bg-gray-900 border-r border-white/5 transition-all duration-300 flex-shrink-0
                h-full
                ${collapsed ? 'w-[64px]' : 'w-[220px]'}
                ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
            `}>
                {/* Logo */}
                <div className="flex items-center justify-between px-3 py-4 border-b border-white/5 min-h-[64px]">
                    {!collapsed && (
                        <button onClick={() => navigate('/')} className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                            </div>
                            <span className="text-[13px] font-bold text-white leading-tight">
                                Med<span className="text-teal-400">Code</span>
                            </span>
                        </button>
                    )}
                    {collapsed && (
                        <div className="w-8 h-8 bg-teal-500 rounded-lg flex items-center justify-center mx-auto">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2"/></svg>
                        </div>
                    )}
                    <button onClick={() => setCollapsed(!collapsed)} className="p-1.5 text-gray-500 hover:text-gray-300 transition-colors hidden md:flex flex-shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            {collapsed ? <path d="M9 18l6-6-6-6"/> : <path d="M15 18l-6-6 6-6"/>}
                        </svg>
                    </button>
                </div>

                {/* User info */}
                {!collapsed && (
                    <div className="px-3 py-3 border-b border-white/5">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-[11px] font-bold text-white">{user?.fullName?.charAt(0).toUpperCase()}</span>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[12px] font-semibold text-white truncate">{user?.fullName}</p>
                                <p className="text-[10px] text-gray-500 truncate">{user?.currentPlan || 'Basic'} Plan</p>
                            </div>
                        </div>
                    </div>
                )}
                {collapsed && (
                    <div className="flex justify-center py-3 border-b border-white/5">
                        <div className="w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center">
                            <span className="text-[11px] font-bold text-white">{user?.fullName?.charAt(0).toUpperCase()}</span>
                        </div>
                    </div>
                )}

                {/* Nav */}
                <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
                    {!collapsed && <p className="px-3 py-1 text-[9px] font-bold text-gray-600 uppercase tracking-widest">Menu</p>}
                    {NAV_ITEMS.map((item, i) => <NavItemLink key={i} item={item}/>)}
                </nav>

                {/* Logout */}
                <div className="p-2 border-t border-white/5">
                    <button onClick={handleLogout}
                        title={collapsed ? 'Logout' : undefined}
                        className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                        {!collapsed && <span className="text-[13px] font-semibold">Logout</span>}
                    </button>
                </div>
            </aside>
        </>
    );
};

export default Sidebar;